import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Box, Flex, Text } from '@theme-ui/components';
import { css, useTheme } from '@emotion/react';

import { getButtonStyles } from '@app/components/Button';
import Link from '@app/components/Link';
import { useGetProductsBySkuQuery } from '@app/pages/ProductFinder/FeaturedProducts/__graphql__/getProductsBySku.query';
import type { AppTheme } from '@app/styles/theme';
import { laptop } from '@app/styles/theme/breakpoints';
import { rem } from '@app/styles/theme/layout';

import messages from './PricingPageHeader.messages';

const PricesSection = ({ sku }: { sku?: string }) => {
  const { url_key, language = 'en' } = useParams<{
    url_key: string;
    language: string;
  }>();
  const theme = useTheme();

  const { data: getProductsBySkuData, loading: getProductsBySkuLoading } =
    useGetProductsBySkuQuery({
      variables: {
        condition: { in: { key: 'objectID', value: [sku] } },
        language,
      },
    });

  if (getProductsBySkuLoading) return null;

  const prices = getProductsBySkuData?.getProducts?.result?.items?.[0]?.price;

  return prices ? (
    <Flex
      data-testid="pricing-page-header-prices"
      pr={rem(32)}
      sx={{ alignItems: 'center' }}
      css={pricesWrapperStyles}
    >
      {prices?.bundle?.originalPrice && (
        <Box mr={4} sx={{ textAlign: 'left' }}>
          <Text sx={{ display: 'block', fontSize: rem(12) }} mb={1}>
            <FormattedMessage {...messages.allProducts} />
          </Text>
          <Box
            color={theme.colors.gradientGreen}
            css={css`
              text-decoration: line-through;
            `}
          >
            <Text
              sx={{ display: 'block' }}
              color={theme.colors.white}
              data-testid="pricing-page-header-all-products-price"
            >
              {prices?.bundle?.originalPrice}
            </Text>
          </Box>
        </Box>
      )}

      {prices?.bundle?.minPrice && (
        <Box mr={4} sx={{ textAlign: 'center' }}>
          <Text sx={{ display: 'block', fontSize: rem(12) }} mb={1}>
            <FormattedMessage {...messages.bundlePrice} />
          </Text>
          <Text
            sx={{ display: 'block' }}
            data-testid="pricing-page-header-bundle-price"
          >
            {prices?.bundle?.minPrice}
          </Text>

          {prices?.bundle?.diffPrice && (
            <Text
              sx={{
                display: 'inline-flex',
                justifyContent: 'space-between',
                fontSize: rem(12),
              }}
              m="0"
              color={theme.colors.gradientGreen}
            >
              <FormattedMessage {...messages.youSave} />
              <Box ml={1} data-testid="pricing-page-header-saving-price">
                {prices?.bundle?.diffPrice}
              </Box>
            </Text>
          )}
        </Box>
      )}

      {prices?.min?.minPrice && (
        <Box mr={4} sx={{ textAlign: 'right' }}>
          <Text sx={{ display: 'block', fontSize: rem(12) }} mb={1}>
            <FormattedMessage {...messages.update} />
          </Text>
          <Text
            sx={{ display: 'block' }}
            data-testid="pricing-page-header-update-price"
          >
            {prices?.min?.minPrice}
          </Text>
        </Box>
      )}

      <Box>
        <Link
          to={`/:language/pricing/${url_key}/`}
          data-testid="pricing-page-header-buy-button"
          css={(theme: AppTheme) => getButtonStyles({ type: 'primary', theme })}
        >
          <FormattedMessage {...messages.buyNow} />
        </Link>
      </Box>
    </Flex>
  ) : null;
};

export default PricesSection;

const pricesWrapperStyles = css`
  position: absolute;
  right: 0;

  @media screen and (max-width: ${laptop}) {
    display: none;
  }
`;
